import React, {ReactNode} from "react";
import {Box, Stack, Typography, useTheme} from "@mui/material";
import {Overview, OverviewSettings, PeriodFigures} from "arteco-api-client-ts";
import {SoftChip, Tone, panelSx} from "../../../../components/kit";

// ── Formato ──────────────────────────────────────────────────────────────────────────────────

// Con separador de miles también en cuatro cifras: es-ES no lo pone por defecto («7260 €» al lado de «117.688 €»).
const GROUP = {useGrouping: "always"} as unknown as Intl.NumberFormatOptions;
export const eur0 = (n?: number | null) => n === undefined || n === null ? "—"
	: n.toLocaleString("es-ES", {...GROUP, maximumFractionDigits: 0}) + " €";
export const num1 = (n?: number | null) => n === undefined || n === null ? "—"
	: n.toLocaleString("es-ES", {minimumFractionDigits: 1, maximumFractionDigits: 1});
export const pct1 = (n?: number | null) => n === undefined || n === null ? "—" : num1(n) + " %";
export const signed = (n: number, text: string) => (n > 0 ? "+" : n < 0 ? "−" : "") + text;
/** «1 factura», «3 facturas» (#14743). */
export const plural = (n: number | undefined | null, one: string, many: string) => `${n || 0} ${(n || 0) === 1 ? one : many}`;
export const dateEs = (d?: string) => d ? new Date(d + "T00:00:00").toLocaleDateString("es-ES") : "—";

// ── Semáforos: verde si cumple; ámbar cerca; rojo lejos ─────────────────────────────────────

/** Margen: verde si llega; ámbar a menos de 5 puntos; rojo por debajo. */
export const marginTone = (v: number, s: OverviewSettings): Tone =>
	v >= (s.minMarginPct ?? 35) ? "success" : v >= (s.minMarginPct ?? 35) - 5 ? "warning" : "error";
/** Días de cobro: verde por debajo; ámbar hasta 10 días por encima; rojo más allá. */
export const daysTone = (v: number, s: OverviewSettings): Tone =>
	v < (s.maxCollectionDays ?? 45) ? "success" : v <= (s.maxCollectionDays ?? 45) + 10 ? "warning" : "error";
/** Peso de un cliente: verde por debajo; ámbar hasta 10 puntos por encima; rojo más allá. */
export const concentrationTone = (v: number, s: OverviewSettings): Tone =>
	v <= (s.maxCustomerPct ?? 40) ? "success" : v <= (s.maxCustomerPct ?? 40) + 10 ? "warning" : "error";

// ── Piezas ───────────────────────────────────────────────────────────────────────────────────

/** Tendencia mínima: la serie y, si hay objetivo, su línea discontinua en verde. */
export const Sparkline = ({values, target, tone = "neutral"}: { values: number[], target?: number, tone?: Tone }) => {
	const theme = useTheme();
	if (values.length < 2) {
		return null;
	}
	const all = target !== undefined ? [...values, target] : values;
	const min = Math.min(...all);
	const max = Math.max(...all);
	const range = max - min || 1;
	const y = (v: number) => 33 - (v - min) / range * 30;
	const x = (i: number) => i / (values.length - 1) * 120;
	const stroke = tone === "error" ? theme.palette.error.main : theme.palette.primary.main;
	return (
		<Box component="svg" viewBox="0 0 120 36" preserveAspectRatio="none" sx={{width: "100%", height: 36, display: "block"}}
			 role="img" aria-label="Tendencia de los últimos periodos">
			{target !== undefined &&
				<line x1="0" x2="120" y1={y(target)} y2={y(target)} stroke={theme.palette.success.main} strokeDasharray="3 3" strokeWidth="1"/>}
			<polyline fill="none" stroke={stroke} strokeWidth="2" points={values.map((v, i) => `${x(i)},${y(v)}`).join(" ")}/>
			<circle cx={x(values.length - 1)} cy={y(values[values.length - 1])} r="2.5" fill={stroke}/>
		</Box>
	);
};

const Delta = ({value, good}: { value: ReactNode, good?: boolean }) => (
	<Typography variant="body2" component="span" fontWeight={700}
				sx={{color: good === undefined ? "text.disabled" : good ? "success.main" : "error.main", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"}}>
		{value}
	</Typography>
);

export interface Comparison {
	label: string;
	value: ReactNode;
	good?: boolean;
}

export const KpiCard = ({label, value, unit, chip, comparisons, trend, note, children}: {
	label: string, value: ReactNode, unit?: ReactNode, chip?: ReactNode, comparisons: Comparison[],
	trend?: ReactNode, note: ReactNode, children?: ReactNode,
}) => (
	<Box sx={{...panelSx, borderRadius: 2.5, bgcolor: "background.paper", p: 2, display: "flex", flexDirection: "column", gap: 1.25, minWidth: 0}}>
		<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
			<Typography variant="body2" color="text.secondary" fontWeight={600}>{label}</Typography>
			{chip}
		</Stack>
		<Typography component="div" fontWeight={700} sx={{fontSize: 26, lineHeight: 1.1, fontVariantNumeric: "tabular-nums"}}>
			{value}
			{unit && <Typography component="span" variant="body2" color="text.secondary" fontWeight={600} sx={{ml: 0.75}}>{unit}</Typography>}
		</Typography>
		{children}
		<Box sx={{display: "grid", gridTemplateColumns: "1fr auto", columnGap: 1.25, rowGap: 0.25}}>
			{comparisons.map((c, i) => (
				<React.Fragment key={i}>
					<Typography variant="body2" color="text.secondary">{c.label}</Typography>
					<Delta value={c.value} good={c.good}/>
				</React.Fragment>
			))}
		</Box>
		{trend}
		<Typography variant="caption" color="text.secondary" sx={{borderTop: "1px solid", borderColor: "divider", pt: 1, mt: "auto"}}>
			{note}
		</Typography>
	</Box>
);

const NoHistory = ({children}: { children: ReactNode }) => (
	<Typography variant="caption" color="text.secondary" component="div"
				sx={{bgcolor: "grey.50", border: "1px dashed", borderColor: "divider", borderRadius: 2, px: 1.25, py: 1}}>
		{children}
	</Typography>
);

const grid2 = {display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))"}, gap: 2} as const;

export const GroupTitle = ({title, hint}: { title: string, hint: string }) => (
	<Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap" useFlexGap sx={{mb: 1.25}}>
		<Typography variant="subtitle1" component="h2" fontWeight={700}>{title}</Typography>
		<Typography variant="caption" color="text.secondary">{hint}</Typography>
	</Stack>
);

// ── Resultado del periodo ────────────────────────────────────────────────────────────────────

const pctChange = (now: number, before: number) => before === 0 ? undefined : (now - before) / Math.abs(before) * 100;

export const ResultKpis = ({o}: { o: Overview }) => {
	const s = o.settings!;
	const c = o.current!;
	const p = o.previous;
	const ly = o.lastYear;
	const trend = o.trend || [];
	const isYear = o.granularity === "YEAR";
	// En un año, el anterior y el del año pasado son el mismo: una sola comparación.
	const others: PeriodFigures[] = (isYear ? [ly] : [p, ly]).filter((x): x is PeriodFigures => !!x);
	const vs = (x: PeriodFigures) => `frente a ${x.label}`;

	const revenueCmp = others.map(x => {
		const d = pctChange(c.revenue || 0, x.revenue || 0);
		return {label: vs(x), value: d === undefined ? "—" : signed(d, pct1(Math.abs(d))), good: d === undefined ? undefined : d >= 0};
	});
	const marginCmp = others.map(x => {
		const d = (c.marginPct || 0) - (x.marginPct || 0);
		return {label: vs(x), value: signed(d, num1(Math.abs(d)) + " pts"), good: d >= 0};
	});
	const overdueCmp = others.map(x => {
		const before = x.overdueAtEnd || 0;
		const d = pctChange(c.overdueAtEnd || 0, before);
		return {
			label: `frente al ${dateEs(x.to)}`,
			value: d === undefined ? (before === 0 ? "0 € entonces" : "—") : signed(d, pct1(Math.abs(d))),
			good: d === undefined ? (c.overdueAtEnd || 0) === 0 : d <= 0,
		};
	});
	const concentrationCmp = others.map(x => {
		const d = (c.topCustomerPct || 0) - (x.topCustomerPct || 0);
		return {label: vs(x), value: signed(d, num1(Math.abs(d)) + " pts"), good: d <= 0};
	});
	const marginT = marginTone(c.marginPct || 0, s);
	const concentrationT = concentrationTone(c.topCustomerPct || 0, s);

	return (
		<Box>
			<GroupTitle title={`Resultado de ${c.label}`} hint="Cambia con el selector de periodo"/>
			<Box sx={grid2}>
				<KpiCard label="Facturado" value={eur0(c.revenue)} chip={<SoftChip tone="neutral" label="Objetivo por definir"/>}
						 comparisons={revenueCmp}
						 trend={<Sparkline values={trend.map(t => t.revenue || 0)}/>}
						 note={`Últimos ${trend.length} periodos, sin IVA. El abono del rápel cuenta en diciembre del año que liquida.`}/>
				<KpiCard label="Margen bruto" value={pct1(c.marginPct)} unit={eur0(c.margin)}
						 chip={<SoftChip tone={marginT} label={`Objetivo ≥ ${num1(s.minMarginPct)} %`}/>}
						 comparisons={marginCmp}
						 trend={<Sparkline values={trend.map(t => t.marginPct || 0)} target={s.minMarginPct}/>}
						 note="Facturado menos coste de empresa de las nóminas, en el mes de su servicio."/>
				<KpiCard label="Cobros vencidos al cierre" value={eur0(c.overdueAtEnd)} unit={plural(c.overdueAtEndCount, "factura", "facturas")}
						 chip={<SoftChip tone="neutral" label="Objetivo por definir"/>}
						 comparisons={overdueCmp}
						 trend={<Sparkline values={trend.map(t => t.overdueAtEnd || 0)} tone="error"/>}
						 note="Sin IVA, vencido y sin cobrar el último día del periodo; las dudosas, aparte. El objetivo de días mide de la emisión al cobro lo cobrado en el periodo.">
					<Box>
						{c.collectionDays === undefined || c.collectionDays === null
							? <SoftChip tone="neutral" label="Sin cobros en el periodo"/>
							: <SoftChip tone={daysTone(c.collectionDays, s)}
										label={`Días de cobro: ${num1(c.collectionDays)} · objetivo < ${num1(s.maxCollectionDays)}`}/>}
					</Box>
				</KpiCard>
				<KpiCard label="Concentración en un cliente" value={pct1(c.topCustomerPct)} unit={c.topCustomerName}
						 chip={<SoftChip tone={concentrationT} label={`Objetivo ≤ ${num1(s.maxCustomerPct)} %`}/>}
						 comparisons={concentrationCmp}
						 trend={<Sparkline values={trend.map(t => t.topCustomerPct || 0)} target={s.maxCustomerPct}
										   tone={concentrationT === "error" ? "error" : "neutral"}/>}
						 note="Lo facturado al primer cliente sobre el total. Bajar es bueno, de ahí el verde."/>
			</Box>
		</Box>
	);
};

// ── Situación a hoy ──────────────────────────────────────────────────────────────────────────

export const TodayKpis = ({o}: { o: Overview }) => {
	const s = o.settings!;
	const t = o.today!;
	const f = t.forecast;
	const forecastT = marginTone(f?.marginPct || 0, s);
	const afterRebateT = marginTone(f?.marginAfterRebatePct || 0, s);
	const d = (f?.marginPct || 0) - (t.previousYearMarginPct || 0);
	return (
		<Box>
			<GroupTitle title={`Situación a hoy, ${dateEs(t.date)}`} hint="No cambia con el selector"/>
			<Box sx={grid2}>
				<KpiCard label="Aceptado sin facturar" value={eur0(t.acceptedNotBilled)} unit={plural(t.acceptedNotBilledCount, "presupuesto", "presupuestos")}
						 chip={<SoftChip tone="neutral" label="Objetivo por definir"/>}
						 comparisons={[
							 {label: "Vencido hoy, con IVA", value: `${eur0(t.overdue)} · ${plural(t.overdueCount, "factura", "facturas")}`, good: (t.overdue || 0) === 0},
							 {label: "Techo libre de las bolsas", value: eur0(t.ceilingLeft)},
						 ]}
						 trend={<NoHistory>Sin histórico: la comparación y la tendencia empiezan cuando se guarde la foto de cada cierre de mes.</NoHistory>}
						 note="Presupuestos aceptados; en las bolsas, lo consumido sin facturar."/>
				<KpiCard label={`Previsión de cierre ${f ? new Date(t.date + "T00:00:00").getFullYear() : ""}`}
						 value={pct1(f?.marginPct)} unit={`${eur0(f?.revenue)} · margen ${eur0(f?.margin)}`}
						 chip={<SoftChip tone={forecastT} label={`Objetivo ≥ ${num1(s.minMarginPct)} %`}/>}
						 comparisons={[
							 {label: "estimación frente al cierre real del año pasado", value: signed(d, num1(Math.abs(d)) + " pts"), good: d >= 0},
							 {label: "tras la provisión de rápel", value: <SoftChip tone={afterRebateT} label={pct1(f?.marginAfterRebatePct)}/>},
						 ]}
						 trend={<NoHistory>Sin histórico de previsiones: la comparación con la del periodo anterior empieza cuando se guarde cada una.</NoHistory>}
						 note="Hipótesis: meses cerrados más el ritmo de facturación y nóminas de los tres últimos, repetido en los que faltan."/>
			</Box>
		</Box>
	);
};
